import { useCallback, useEffect, useRef, useState } from 'react';
import { selectRecorder } from '../../platform/recorder';
import { transcribeRecording } from '../../data/stt-client';

/**
 * VOICE TYPING — the shared, feature-agnostic primitive. M9 / S09-04.
 *
 *   RecorderPort → temporary audio Blob → Crewlink's own STT endpoint → text
 *
 * **IT PRODUCES EDITABLE TEXT AND NOTHING ELSE.** It does not submit anything,
 * ask anything, create a Capture, create a Reminder, infer an intent or route
 * anywhere. Whatever it hands back is ordinary text the caller puts in a field,
 * and a person decides what happens next.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT REUSES, AND WHAT IT DELIBERATELY DOES NOT
 * ---------------------------------------------------------------------------
 *
 * **REUSED, because Crewlink already owns both and a second copy would be a
 * second set of bugs:** `RecorderPort` (`src/platform/recorder.ts`) and
 * `transcribeRecording` (`src/data/stt-client.ts`). The transcription client is
 * already feature-agnostic — it takes a Blob and returns text — and it already
 * names no vendor, holds no key and reaches no provider directly (D-012).
 *
 * **NOT REUSED, because it belongs to Capture and dragging it in would make
 * every dictating screen a Capture screen:** draft persistence and the D-010
 * crash-recovery lifecycle · the ten-minute recording cap · keep-awake ·
 * interruption-to-recovery · the review-and-accept flow · Work Log events ·
 * any retention of the original audio.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A SEPARATE MODULE AND NOT PART OF ASK SOL
 * ---------------------------------------------------------------------------
 *
 * Jerry has locked a future capstone: **one global Sol with one universal
 * microphone**, connected across the finished app. That is NOT this — this is
 * voice typing for one text field. But it is the plumbing the capstone will
 * need, so it lives where the capstone can reuse it rather than inside a screen
 * it would have to be cut out of.
 *
 * **AND IT MUST STAY THAT SHALLOW.** No intent routing, no action dispatch, no
 * command parsing, no wake word. The moment this module decides what a sentence
 * MEANS, it has become a second, unreviewed global microphone.
 *
 * ---------------------------------------------------------------------------
 * PRIVACY
 * ---------------------------------------------------------------------------
 *
 * Audio goes to Crewlink's own authenticated endpoint, exactly as a Capture's
 * does. **No new provider surface, no key in the browser, no direct
 * browser-to-provider call**, and no new retention category — the transcription
 * endpoint's posture is the one D-011 already records. Nothing here changes
 * D-011's status.
 */

export type DictationState =
  | 'idle'
  | 'recording'
  | 'stopping'
  | 'transcribing'
  /** The microphone was refused. Typing still works. */
  | 'denied'
  /** This runtime cannot record at all. Typing still works. */
  | 'unavailable'
  /** Recorded, but the words did not come back. Typing still works. */
  | 'failed';

export interface Dictation {
  readonly state: DictationState;
  /** Crewlink's own wording for the current failure, or null. */
  readonly error: string | null;
  /** Whether this runtime has a recorder at all. Says nothing about permission. */
  readonly isSupported: boolean;
  /** True while the microphone is live or its audio is still being turned into text. */
  readonly isBusy: boolean;
  start(): void;
  stop(): void;
  /** Abandon whatever is in flight and release the microphone. */
  cancel(): void;
}

export interface DictationOptions {
  /** Given the words. **The caller decides what they become.** */
  readonly onTranscript: (transcript: string) => void;
  /**
   * Called before the microphone opens.
   *
   * **THIS IS THE AUDIO-EXCLUSIVITY SEAM.** A phone recording while Crewlink is
   * talking records Crewlink talking. The caller stops its own playback here;
   * this module deliberately knows nothing about what else makes sound.
   */
  readonly onBeforeStart?: () => void;
}

const DENIED = 'Crewlink needs microphone access to take a spoken question.';
const UNAVAILABLE = 'This device cannot record audio.';
const EMPTY_RECORDING = 'Nothing was recorded. Try again.';

export function useDictation(options: DictationOptions): Dictation {
  const [recorder] = useState(() => selectRecorder());
  const [state, setState] = useState<DictationState>('idle');
  const [error, setError] = useState<string | null>(null);

  /**
   * Operation generation. **Every asynchronous continuation checks it**, so a
   * transcript belonging to a recording that was cancelled — or to a screen that
   * has since been left — cannot arrive and overwrite what he is typing now.
   */
  const operation = useRef(0);
  const mounted = useRef(true);

  // Taken from the options directly and carried in the dependency arrays below.
  // The "latest callback in a ref" trick would mean writing a ref during render,
  // and capturing the value from the render that started the recording is the
  // correct behaviour here anyway.
  const { onTranscript, onBeforeStart } = options;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current += 1;
      // **LEAVING THE SCREEN RELEASES THE MICROPHONE.** The I-021 precedent: a
      // recording nobody is watching is a live microphone indicator and a
      // pointless transcription bill.
      recorder.cancel();
    };
  }, [recorder]);

  const start = useCallback(() => {
    if (state !== 'idle' && state !== 'denied' && state !== 'failed') return;

    // Whatever else is making sound stops first. See `onBeforeStart`.
    onBeforeStart?.();

    if (!recorder.isSupported()) {
      setState('unavailable');
      setError(UNAVAILABLE);
      return;
    }

    const mine = operation.current + 1;
    operation.current = mine;
    setState('recording');
    setError(null);

    void recorder.start().catch(() => {
      if (!mounted.current || mine !== operation.current) return;
      // A refused permission and a failed open are the same thing to Brandon:
      // no microphone, and typing still works.
      setState('denied');
      setError(DENIED);
    });
  }, [onBeforeStart, recorder, state]);

  const stop = useCallback(() => {
    if (state !== 'recording') return;
    const mine = operation.current;
    setState('stopping');

    void (async () => {
      try {
        const recording = await recorder.stop();
        if (!mounted.current || mine !== operation.current) return;

        if (recording.byteSize === 0) {
          setState('failed');
          setError(EMPTY_RECORDING);
          return;
        }

        setState('transcribing');
        const outcome = await transcribeRecording(recording.blob);
        if (!mounted.current || mine !== operation.current) return;

        if (outcome.transcript === null) {
          // `transcribeRecording` is total and its message is already Brandon's
          // English. **No provider name and no internal code can appear in it.**
          setState('failed');
          setError(outcome.error);
          return;
        }

        setState('idle');
        setError(null);
        // **THE ONLY THING THIS MODULE DOES WITH THE WORDS.** It hands them over.
        onTranscript(outcome.transcript);
      } catch {
        if (!mounted.current || mine !== operation.current) return;
        setState('failed');
        setError(EMPTY_RECORDING);
      }
    })();
  }, [onTranscript, recorder, state]);

  const cancel = useCallback(() => {
    operation.current += 1;
    recorder.cancel();
    setState('idle');
    setError(null);
  }, [recorder]);

  return {
    state,
    error,
    isSupported: recorder.isSupported(),
    isBusy: state === 'recording' || state === 'stopping' || state === 'transcribing',
    start,
    stop,
    cancel,
  };
}
